import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, ChevronDown, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiChangePassword, setToken } from "@/lib/api";
import { PasswordChecklist, passwordValid } from "@/components/PasswordChecklist";

function PwInput({ value, onChange, placeholder, testid, autoComplete }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        autoComplete={autoComplete} data-testid={testid}
        className="w-full bg-surface border border-hair rounded-lg pl-3 pr-10 py-2.5 text-sm text-onSurface placeholder:text-subtle focus:outline-none focus:border-brand"
      />
      <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Nascondi password" : "Mostra password"} data-testid={`${testid}-toggle`}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-subtle hover:text-brand">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export function PasswordCard() {
  const { user, setUser } = useAuth();
  const hasPw = !!user?.has_password;
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (hasPw && !cur) return toast.error("Inserisci la password attuale");
    if (!passwordValid(pw)) return toast.error("La nuova password non rispetta i requisiti indicati");
    if (pw !== pw2) return toast.error("Le due password non coincidono");
    setBusy(true);
    try {
      const r = await apiChangePassword({ current_password: hasPw ? cur : null, new_password: pw });
      setToken(r.token);
      setUser(r.user);
      setCur(""); setPw(""); setPw2(""); setOpen(false);
      toast.success(hasPw ? "Password aggiornata. Gli altri dispositivi sono stati disconnessi." : "Password impostata: ora puoi accedere anche con email e password.");
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const title = hasPw ? "Cambia password" : "Imposta una password";
  return (
    <div data-testid="password-card" className="mt-4 bg-surfaceSecondary border border-hair rounded-2xl">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="password-card-toggle"
        className="w-full flex items-center gap-3 px-5 py-4 text-left">
        <KeyRound size={18} className="text-brand shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-onSurface">{title}</span>
          {!hasPw && <span className="block text-xs text-subtle mt-0.5">Accedi con Google: imposta una password per entrare anche con la tua email.</span>}
        </span>
        <ChevronDown size={18} className={`text-subtle transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form onSubmit={submit} data-testid="password-form" className="px-5 pb-5 space-y-2.5">
          {hasPw && <PwInput value={cur} onChange={setCur} placeholder="Password attuale" testid="password-current" autoComplete="current-password" />}
          <PwInput value={pw} onChange={setPw} placeholder="Nuova password" testid="password-new" autoComplete="new-password" />
          <PasswordChecklist value={pw} testid="password-new-rules" />
          <PwInput value={pw2} onChange={setPw2} placeholder="Conferma nuova password" testid="password-confirm" autoComplete="new-password" />
          <p className="text-[11px] text-subtle">Per sicurezza, dopo il salvataggio verrai disconnesso dagli altri dispositivi.</p>
          <button type="submit" disabled={busy || !passwordValid(pw) || pw !== pw2 || (hasPw && !cur)} data-testid="password-submit"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary disabled:opacity-60 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} {hasPw ? "Aggiorna password" : "Imposta password"}
          </button>
        </form>
      )}
    </div>
  );
}
